import axios from 'axios';
import pathfinder from '@fiverr-private/pathfinder';
import { logger } from '@fiverr-private/obs';
import { HttpStatusCode } from '../constants';

export const getResendActivationUrl = () => pathfinder('reactivate');
export const getUserIsPendingUrl = () => pathfinder('user_is_pending');
export const getUsernameAndEmailUrl = () => pathfinder('user_name_and_email');

/**
 * Resends activation email to the specified email address.
 * Also, it updates the email address of the current user
 * in case it is different from the original current user's email.
 * @param {string} email the user's email address.
 * @return {Promise<object>} with success property to indicate weather the call succeeded.
 */
export const resendActivationEmail = async(email) => {
    const response = { success: true };
    const payload = { email };
    const resendActivationUrl = getResendActivationUrl();

    try {
        const { status } = await axios.post(resendActivationUrl, payload);

        if (status >= HttpStatusCode.BAD_REQUEST) {
            response.success = false;
        }
    } catch (error) {
        logger.error(error, {
            msg: 'Error while resending activation email',
            url: resendActivationUrl,
            payload: JSON.stringify(payload)
        });
        response.success = false;
    }

    return response;
};

/**
 * Get if current user is pending. Throw error if error occurred during the API request.
 * @returns {Promise<boolean>}
 * @throws {Error}
 */
export const userIsPending = async() => {
    const userIsPendingUrl = getUserIsPendingUrl();

    try {
        const { data } = await axios.get(userIsPendingUrl);
        const { pending } = data;

        return !!pending;
    } catch (error) {
        logger.error(error, {
            msg: 'Error while checking if user is pending',
            url: userIsPendingUrl
        });
        throw error;
    }
};

/**
 * Get username and email of current user. Throw error if error occurred during the API request.
 * @returns {Promise<object>}
 * @throws {Error}
 */
export const userNameAndEmail = async() => {
    const usernameAndEmailUrl = getUsernameAndEmailUrl();

    try {
        const { data } = await axios.get(usernameAndEmailUrl);
        const { username, email } = data;

        return { username, email };
    } catch (error) {
        logger.error(error, {
            msg: 'Error while getting user username and email',
            url: usernameAndEmailUrl
        });
        throw error;
    }
};
